import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
  MdAssignment,
  MdPerson,
  MdFilterList,
  MdSearch,
  MdClose,
  MdVisibility,
  MdEdit,
  MdDelete,
  MdBarChart,
  MdVerifiedUser,
  MdBuild,
  MdTrendingUp,
  MdSecurity,
  MdSave,
} from "react-icons/md";
import { useWorkers } from "context/WorkersContext";
import { useSectorKpis } from "context/SectorKpiContext";
import { ROLES } from "context/WorkersContext";
import { getEvaluationScores } from "api/evaluationScores";

const PRIMARY = "#0e76a8";
const SECONDARY = "#f27405";

const KPI_ICONS = [MdBarChart, MdVerifiedUser, MdBuild, MdTrendingUp, MdSecurity];

// Format a stored ISO date: if time is exactly midnight, show only the date.
const formatRecordDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  if (!Number.isFinite(d.getTime())) return dateStr;
  const h = d.getHours();
  const m = d.getMinutes();
  const s = d.getSeconds();
  if (h === 0 && m === 0 && s === 0) {
    return d.toLocaleDateString();
  }
  return d.toLocaleString();
};

function ViewDetailsModal({ ev, workers, kpis, onClose }) {
  const supervisor = workers.find((w) => w.id === ev.supervisorId);

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" aria-hidden onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="view-details-title"
      >
        <div className="shrink-0 flex items-center justify-between gap-4 p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800">
          <h2 id="view-details-title" className="text-lg sm:text-xl font-black text-slate-900 dark:text-white">
            Evaluation details
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
            aria-label="Close"
          >
            <MdClose className="text-xl" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500 dark:text-slate-400 font-medium">Evaluatee</p>
              <p className="font-bold text-slate-900 dark:text-white">
                {ev.evaluateeName || ev.operatorName || "—"}
                {(ev.evaluateeRole || ROLES.OPERATOR) !== ROLES.OPERATOR && (
                  <span className="ml-2 text-xs font-normal text-slate-400">(Supervisor)</span>
                )}
              </p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400 font-medium">Sector</p>
              <p className="font-bold text-slate-900 dark:text-white">{ev.sectorName || "—"}</p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400 font-medium">Supervisor</p>
              <p className="font-bold text-slate-900 dark:text-white">{supervisor?.name ?? "—"}</p>
            </div>
            <div>
              <p className="text-slate-500 dark:text-slate-400 font-medium">Date</p>
              <p className="font-bold text-slate-900 dark:text-white">{formatRecordDate(ev.date)}</p>
            </div>
          </div>
          <div>
            <h3 className="font-bold text-slate-800 dark:text-slate-200 text-sm uppercase tracking-wider mb-3">
              KPI scores & notes
            </h3>
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                    <th className="px-4 py-3 text-left font-bold text-slate-600 dark:text-slate-300">KPI</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-600 dark:text-slate-300 w-20">Score</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-600 dark:text-slate-300 w-20">Score (%)</th>
                    <th className="px-4 py-3 text-left font-bold text-slate-600 dark:text-slate-300">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(ev.kpiNames || {}).map((kpiId) => {
                    const kpiDefinition = kpis.find(k => k.id === kpiId)?.definition;
                    const kpiName = ev.kpiNames[kpiId];
                    const score = ev.kpiScores[kpiId];
                    const percentageScore = ev.kpiPercentageScores[kpiId];
                    // Notes are not available from the API in the grouped record structure currently
                    const notes = "—"; // Placeholder for now
                    const Icon = KPI_ICONS[kpis.findIndex(k => k.id === kpiId)] || MdBarChart;
                    const iconColor = kpis.find(k => k.id === kpiId)?.iconColor || "text-slate-500";

                    return (
                      <tr key={kpiId} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1.5 ${iconColor}`}>
                            <Icon className="text-base shrink-0" />
                            <span className="font-medium text-slate-900 dark:text-white">{kpiName}</span>
                          </span>
                          {kpiDefinition && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{kpiDefinition}</p>
                          )}
                        </td>
                        <td className="px-4 py-3 font-mono font-bold text-slate-800 dark:text-slate-200">
                          {score != null ? score : "—"}
                        </td>
                        <td className="px-4 py-3 font-mono font-bold text-slate-800 dark:text-slate-200">
                          {percentageScore != null ? `${percentageScore}%` : "—"}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-400">
                          {notes}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="shrink-0 p-4 sm:p-6 border-t border-slate-100 dark:border-slate-800 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}

function EditEvaluationModal({ ev, kpis, onClose, onSave }) {
  const [scores, setScores] = useState({});
  const [percentageScores, setPercentageScores] = useState({});
  const [kpiNotes, setKpiNotes] = useState({});

  useEffect(() => {
    if (ev) {
      setScores({ ...(ev.kpiScores || {}) });
      setPercentageScores({ ...(ev.kpiPercentageScores || {}) });
      setKpiNotes({ ...(ev.kpiNotes || {}) });
    }
  }, [ev]);

  const handleScoreChange = (kpiId, value) => {
    const num = parseInt(value, 10);
    if (num >= 1 && num <= 5) setScores((prev) => ({ ...prev, [kpiId]: num }));
  };

  const handlePercentageScoreChange = (kpiId, value) => {
    const str = String(value).trim();
    if (str === "") {
      setPercentageScores((prev) => {
        const next = { ...prev };
        delete next[kpiId];
        return next;
      });
      return;
    }
    const num = parseFloat(str);
    if (!Number.isNaN(num)) {
      const clamped = Math.min(100, Math.max(0, num));
      setPercentageScores((prev) => ({ ...prev, [kpiId]: clamped }));
    }
  };

  const handleNoteChange = (kpiId, text) => {
    setKpiNotes((prev) => ({ ...prev, [kpiId]: text }));
  };

  const allFilled = kpis.every((k) => scores[k.id] >= 1 && scores[k.id] <= 5);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!allFilled || !ev?.id) return;
    onSave(ev.id, { kpiScores: { ...scores }, kpiPercentageScores: { ...percentageScores }, kpiNotes: { ...kpiNotes } });
    onClose();
  };

  if (!ev) return null;

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" aria-hidden onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl max-h-[90vh] flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-eval-title"
      >
        <div className="shrink-0 flex items-center justify-between gap-4 p-4 sm:p-6 border-b border-slate-100 dark:border-slate-800">
          <h2 id="edit-eval-title" className="text-lg sm:text-xl font-black text-slate-900 dark:text-white">
            Edit evaluation
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
            aria-label="Close"
          >
            <MdClose className="text-xl" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
              {ev.evaluateeName || ev.operatorName} · {ev.sectorName}
            </p>
            <div className="space-y-4">
              {kpis.map((kpi, idx) => {
                const Icon = KPI_ICONS[idx] || MdBarChart;
                const iconColor = kpi.iconColor || "text-slate-500";
                return (
                  <div
                    key={kpi.id}
                    className="p-4 rounded-xl border border-slate-200 dark:border-slate-700 flex flex-col sm:flex-row gap-4 items-start"
                  >
                    <div className="w-full sm:w-1/3 shrink-0">
                      <div className={`flex items-center gap-2 mb-1 ${iconColor}`}>
                        <Icon className="text-lg shrink-0" />
                        <h4 className="font-bold text-slate-900 dark:text-white text-sm">{kpi.name}</h4>
                      </div>
                    </div>
                    <div className="flex-1 w-full flex flex-col gap-2">
                      <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-xl w-full max-w-xs gap-0.5">
                        {[1, 2, 3, 4, 5].map((n) => (
                          <React.Fragment key={n}>
                            <input
                              type="radio"
                              name={`edit-score-${kpi.id}`}
                              id={`edit-${kpi.id}-${n}`}
                              checked={scores[kpi.id] === n}
                              onChange={() => handleScoreChange(kpi.id, n)}
                              className="sr-only"
                            />
                            <label
                              htmlFor={`edit-${kpi.id}-${n}`}
                              className={`flex-1 text-center py-2 text-sm font-bold rounded-lg cursor-pointer transition-all ${
                                scores[kpi.id] === n ? "text-white shadow-sm" : "text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700"
                              }`}
                              style={scores[kpi.id] === n ? { backgroundColor: SECONDARY } : {}}
                            >
                              {n}
                            </label>
                          </React.Fragment>
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <label htmlFor={`edit-percentage-${kpi.id}`} className="text-sm font-bold text-slate-600 dark:text-slate-400 whitespace-nowrap">Score (%)</label>
                        <input
                          id={`edit-percentage-${kpi.id}`}
                          type="number"
                          min={0}
                          max={100}
                          step={0.5}
                          value={percentageScores[kpi.id] ?? ""}
                          onChange={(e) => handlePercentageScoreChange(kpi.id, e.target.value)}
                          placeholder="0–100"
                          className="w-24 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm px-3 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:outline-none placeholder-slate-400 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                      <textarea
                        value={kpiNotes[kpi.id] ?? ""}
                        onChange={(e) => handleNoteChange(kpi.id, e.target.value)}
                        placeholder={`Notes for ${kpi.name}…`}
                        rows={2}
                        className="w-full text-sm rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white px-4 py-2.5 focus:ring-2 focus:ring-[#0e76a8]/30 focus:outline-none placeholder-slate-400 resize-none"
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="shrink-0 p-4 sm:p-6 border-t border-slate-100 dark:border-slate-800 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!allFilled}
              className="px-6 py-2.5 rounded-xl text-white font-black text-sm shadow-lg hover:opacity-90 transition-opacity flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: SECONDARY, boxShadow: `${SECONDARY}33 0 10px 20px` }}
            >
              <MdSave className="text-lg" />
              Save changes
            </button>
          </div>
        </form>
      </div>
    </>
  );
}

function DeleteConfirmModal({ ev, onClose, onConfirm }) {
  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" aria-hidden onClick={onClose} />
      <div
        className="fixed left-1/2 top-1/2 z-50 w-[95vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-xl p-6"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="delete-confirm-title"
      >
        <h2 id="delete-confirm-title" className="text-lg font-black text-slate-900 dark:text-white mb-2">
          Delete evaluation record?
        </h2>
        <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
          This will permanently remove the evaluation for{" "}
          <span className="font-bold text-slate-900 dark:text-white">{ev?.evaluateeName ?? ev?.operatorName ?? "this person"}</span> in{" "}
          <span className="font-bold text-slate-900 dark:text-white">{ev?.sectorName ?? "this sector"}</span>.
          This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 font-bold text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700/50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              if (ev?.id) onConfirm(ev.id);
              onClose();
            }}
            className="px-6 py-2.5 rounded-xl font-bold text-sm text-white bg-red-600 hover:bg-red-700 transition-colors flex items-center gap-2"
          >
            <MdDelete className="text-lg" />
            Delete
          </button>
        </div>
      </div>
    </>
  );
}

export default function EvaluationRecords() {
  const { workers } = useWorkers();
  const { getKpisForSectorRole } = useSectorKpis();
  const [search, setSearch] = useState("");
  const [sectorFilter, setSectorFilter] = useState("");
  const [viewEv, setViewEv] = useState(null);
  const [editEv, setEditEv] = useState(null);
  const [deleteEv, setDeleteEv] = useState(null);

  const [apiEvaluationRecords, setApiEvaluationRecords] = useState([]);
  const [evaluationsLoading, setEvaluationsLoading] = useState(true);
  const [evaluationsError, setEvaluationsError] = useState(null);

  const fetchEvaluationRecords = useCallback(async () => {
    setEvaluationsLoading(true);
    setEvaluationsError(null);
    try {
      const result = await getEvaluationScores();
      const flatScores = result.data || [];

      // Group flat API scores into structured evaluation records
      const grouped = flatScores.reduce((acc, score) => {
        const evaluationId = score.evaluation_id;
        if (!acc[evaluationId]) {
          acc[evaluationId] = {
            id: evaluationId,
            evaluateeId: score.evaluatee_id,
            evaluateeName: score.evaluatee_name,
            sectorId: score.sector_id,
            sectorName: score.sector_name,
            evaluateeRole: score.evaluatee_role,
            date: score.evaluated_at, // Use evaluated_at from first KPI as overall date
            kpiScores: {},
            kpiPercentageScores: {},
            kpiNotes: {}, // API doesn't provide notes for now
            kpiNames: {}, // To store KPI names for display in modals
            supervisorId: "", // API does not provide supervisor ID yet
          };
        }
        acc[evaluationId].kpiScores[score.kpi_id] = score.score_range;
        acc[evaluationId].kpiPercentageScores[score.kpi_id] = score.score_percentage;
        acc[evaluationId].kpiNames[score.kpi_id] = score.kpi_name;
        return acc;
      }, {});

      setApiEvaluationRecords(Object.values(grouped));
    } catch (err) {
      setEvaluationsError(err.response?.data?.message || err.message || "Failed to load evaluation records");
      setApiEvaluationRecords([]);
    } finally {
      setEvaluationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvaluationRecords();
  }, [fetchEvaluationRecords]);

  const viewKpis = useMemo(
    () => (viewEv?.sectorId ? getKpisForSectorRole(viewEv.sectorId, viewEv.evaluateeRole || ROLES.OPERATOR) : []),
    [viewEv, getKpisForSectorRole]
  );
  const editKpis = useMemo(
    () => (editEv?.sectorId ? getKpisForSectorRole(editEv.sectorId, editEv.evaluateeRole || ROLES.OPERATOR) : []),
    [editEv, getKpisForSectorRole]
  );

  const sectors = useMemo(
    () => [...new Set(apiEvaluationRecords.map((e) => e.sectorName).filter(Boolean))].sort(),
    [apiEvaluationRecords]
  );

  const filtered = useMemo(() => {
    return apiEvaluationRecords.filter((ev) => {
      const matchSearch =
        !search.trim() ||
        ((ev.evaluateeName || ev.operatorName) && (ev.evaluateeName || ev.operatorName).toLowerCase().includes(search.trim().toLowerCase())) ||
        (ev.sectorName && ev.sectorName.toLowerCase().includes(search.trim().toLowerCase()));
      const matchSector = !sectorFilter || ev.sectorName === sectorFilter;
      return matchSearch && matchSector;
    });
  }, [apiEvaluationRecords, search, sectorFilter]);

  const handleEditSave = useCallback(
    (id, updates) => {
      setApiEvaluationRecords((prev) =>
        prev.map((ev) => (ev.id === id ? { ...ev, ...updates } : ev))
      );
      setEditEv(null);
    },
    []
  );

  const handleDeleteConfirm = useCallback(
    (id) => {
      setApiEvaluationRecords((prev) => prev.filter((ev) => ev.id !== id));
      setDeleteEv(null);
    },
    []
  );

  return (
    <div className="p-4 md:p-6 pt-4 md:pt-6 space-y-6 max-w-[1200px] mx-auto">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-between sm:items-start gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 mb-1">
            <span
              className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-widest"
              style={{ backgroundColor: `${SECONDARY}20`, color: SECONDARY }}
            >
              Evaluation
            </span>
          </div>
          <h2 className="text-slate-900 dark:text-white text-2xl md:text-3xl font-black tracking-tight">
            Evaluation records
          </h2>
          <p className="text-slate-500 dark:text-slate-400 text-sm md:text-base max-w-2xl">
            Records come from evaluations submitted on the Evaluate page. View, edit, or delete entries below.
          </p>
        </div>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 min-w-0">
          <MdSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by operator or sector…"
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white text-sm placeholder-slate-400 focus:ring-2 focus:ring-[#0e76a8]/30 focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-2">
          <MdFilterList className="text-slate-500 shrink-0" />
          <select
            value={sectorFilter}
            onChange={(e) => setSectorFilter(e.target.value)}
            className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-200 text-sm font-medium px-4 py-2.5 min-w-[180px] focus:outline-none focus:ring-2 focus:ring-[#0e76a8]/30"
          >
            <option value="">All sectors</option>
            {sectors.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm overflow-hidden">
        <div className="p-4 sm:p-5 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
          <MdAssignment className="text-xl" style={{ color: PRIMARY }} />
          <h3 className="font-black text-slate-900 dark:text-white">Records</h3>
        </div>
        <div className="overflow-x-auto">
          {evaluationsLoading ? (
            <div className="p-12 text-center">
              <p className="text-sm text-slate-500 dark:text-slate-400">Loading evaluation records…</p>
            </div>
          ) : evaluationsError ? (
            <div className="p-12 text-center bg-red-50 dark:bg-red-900/20 rounded-b-2xl">
              <p className="text-sm text-red-500 dark:text-red-400 mb-2">{evaluationsError}</p>
              <button
                type="button"
                onClick={fetchEvaluationRecords}
                className="text-sm font-bold text-[#0e76a8] hover:underline"
              >
                Retry
              </button>
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center">
              <MdPerson className="mx-auto text-4xl text-slate-300 dark:text-slate-600 mb-3" />
              <p className="text-slate-500 dark:text-slate-400 font-medium">
                {apiEvaluationRecords.length === 0
                  ? "No evaluation records yet. Submit evaluations from the Evaluate page."
                  : "No records match your filters."}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">Evaluatee</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">Sector</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">KPI Names</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">Scores (Range)</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">Scores (%)</th>
                  <th className="px-4 sm:px-6 py-3 text-left text-xs font-black uppercase text-slate-500">Date</th>
                  <th className="px-4 sm:px-6 py-3 text-right text-xs font-black uppercase text-slate-500">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((ev) => (
                  <tr
                    key={ev.id}
                    className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/30"
                  >
                    <td className="px-4 sm:px-6 py-3 font-medium text-slate-900 dark:text-white">
                      <span>{ev.evaluateeName || ev.operatorName}</span>
                      {(ev.evaluateeRole || ROLES.OPERATOR) !== ROLES.OPERATOR && (
                        <span className="ml-1.5 text-[10px] font-bold uppercase text-slate-400">(Supervisor)</span>
                      )}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-slate-600 dark:text-slate-400">{ev.sectorName}</td>
                    <td className="px-4 sm:px-6 py-3">
                      <span className="text-slate-700 dark:text-slate-300 font-mono text-xs">
                        {Object.values(ev.kpiNames || {}).join(", ") || "—"}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3">
                      <span className="text-slate-700 dark:text-slate-300 font-mono text-xs">
                        {Object.values(ev.kpiScores || {}).join(", ") || "—"}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3">
                      <span className="text-slate-700 dark:text-slate-300 font-mono text-xs">
                        {Object.values(ev.kpiPercentageScores || {}).map(s => s != null ? `${s}%` : "—").join(", ") || "—"}
                      </span>
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-slate-500 dark:text-slate-400 text-xs">
                      {formatRecordDate(ev.date)}
                    </td>
                    <td className="px-4 sm:px-6 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => setViewEv(ev)}
                          className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-[#0e76a8] transition-colors"
                          title="View details"
                          aria-label="View details"
                        >
                          <MdVisibility className="text-lg" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditEv(ev)}
                          className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-amber-600 dark:hover:text-amber-400 transition-colors"
                          title="Edit"
                          aria-label="Edit"
                        >
                          <MdEdit className="text-lg" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteEv(ev)}
                          className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title="Delete"
                          aria-label="Delete"
                        >
                          <MdDelete className="text-lg" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {viewEv && (
        <ViewDetailsModal ev={viewEv} workers={workers} kpis={viewKpis} onClose={() => setViewEv(null)} />
      )}
      {editEv && (
        <EditEvaluationModal
          ev={editEv}
          kpis={editKpis}
          onClose={() => setEditEv(null)}
          onSave={handleEditSave}
        />
      )}
      {deleteEv && (
        <DeleteConfirmModal
          ev={deleteEv}
          onClose={() => setDeleteEv(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
}
